import React, { useState, useEffect } from 'react';
import { apiClient } from '../config/api';
import { QuickStats } from '../components/dashboard/QuickStats';
import { DailyComparisonView } from '../components/meal-planning/DailyComparisonView';

interface DailyFoodEntriesPageProps {
  user: any;
  coachMode?: boolean;
  clientId?: number;
}

interface Meal {
  id: number;
  timestamp: string;
  meal_context: string;
  meal_image_path: string;
  analyzed_foods: any[];
  recognition_confidence: number;
  nutrition_totals: any;
  meal_description: string;
  meal_date?: string; // Edited date (optional, falls back to timestamp)
}

export const DailyFoodEntriesPage: React.FC<DailyFoodEntriesPageProps> = ({
  user,
  coachMode = false,
  clientId
}) => {
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filterContext, setFilterContext] = useState<string>('all');
  const [clientProfile, setClientProfile] = useState<any>(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showNoteModal, setShowNoteModal] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [selectedMeal, setSelectedMeal] = useState<Meal | null>(null);
  const [coachNote, setCoachNote] = useState<string>('');

  // Report generation states
  const [showReportModal, setShowReportModal] = useState<boolean>(false);
  const [generatingReport, setGeneratingReport] = useState<boolean>(false);
  const [reportData, setReportData] = useState<any>(null);

  // Comparison view state
  const [showComparisonView, setShowComparisonView] = useState<boolean>(false);

  // CSRF token state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [sessionId, setSessionId] = useState<string>('');

  // Fetch CSRF token on mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const token = localStorage.getItem('auth_token');

        // Generate or retrieve session ID
        let sessionIdValue = localStorage.getItem('session_id');
        if (!sessionIdValue) {
          // Generate a unique session ID
          sessionIdValue = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('session_id', sessionIdValue);
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/csrf-token`, {
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Session-Id': sessionIdValue
          }
        });
        const data = await response.json();
        console.log('CSRF token response:', data);

        if (data.success && (data.csrf_token || data.csrfToken)) {
          const csrfTokenValue = data.csrf_token || data.csrfToken;
          setCsrfToken(csrfTokenValue);
          setSessionId(sessionIdValue);
        } else {
          console.error('Failed to get CSRF token:', data);
        }
      } catch (err) {
        console.error('Failed to fetch CSRF token:', err);
      }
    };
    fetchCsrfToken();
  }, []);

  // Fetch client profile to get nutrient recommendations
  useEffect(() => {
    if (coachMode && clientId) {
      fetchClientProfile();
    }
  }, [clientId]);

  // Fetch meals when date changes
  useEffect(() => {
    fetchMeals();
  }, [selectedDate, clientId]);

  const fetchClientProfile = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/coach/clients/${clientId}/profile`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();
      if (data.success) {
        setClientProfile(data.profile);
      }
    } catch (err) {
      console.error('Failed to fetch client profile:', err);
    }
  };

  const fetchMeals = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const endpoint = coachMode && clientId
        ? `/api/coach/clients/${clientId}/meals`
        : `/api/meals/portfolio`;

      console.log('Fetching meals from:', endpoint);
      console.log('Coach mode:', coachMode, 'Client ID:', clientId);

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
        {
          method: 'GET',
          credentials: 'include', // Include cookies for session-based auth
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response error:', errorText);
        setError(`Failed to fetch meals (${response.status})`);
        setLoading(false);
        return;
      }

      const data = await response.json();
      console.log('Meals data received:', data);

      if (data.success) {
        // Parse JSON fields if they're strings
        const processedMeals = (data.meals || []).map((meal: any) => {
          try {
            return {
              ...meal,
              analyzed_foods: typeof meal.analyzed_foods === 'string'
                ? JSON.parse(meal.analyzed_foods)
                : (meal.analyzed_foods || []),
              nutrition_totals: typeof meal.nutrition_totals === 'string'
                ? JSON.parse(meal.nutrition_totals)
                : (meal.nutrition_totals || {})
            };
          } catch (e) {
            console.warn('Failed to parse meal JSON data:', e, meal);
            return {
              ...meal,
              analyzed_foods: [],
              nutrition_totals: {}
            };
          }
        });

        setMeals(processedMeals);
      } else {
        setError(data.error || 'Failed to fetch meals');
      }
    } catch (err) {
      console.error('Error fetching meals:', err);
      setError(`Failed to load meals: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Filter meals by selected date
  const mealsForSelectedDate = meals.filter(meal => {
    // Use meal_date if available (the intended date set by user)
    // This avoids timezone issues with UTC timestamps
    let mealDate;

    if (meal.meal_date) {
      // Extract just the date part (YYYY-MM-DD) from meal_date
      // meal_date might be "2025-10-29" or "2025-10-29T00:00:00.000Z"
      mealDate = meal.meal_date.split('T')[0];
    } else {
      // Fallback: parse timestamp in user's local timezone
      const date = new Date(meal.timestamp);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      mealDate = `${year}-${month}-${day}`;
    }

    // Debug logging
    if (meals.indexOf(meal) === 0) {
      console.log('🔍 Date Filtering Debug:', {
        selectedDate,
        hasMealDate: !!meal.meal_date,
        mealDateField: meal.meal_date,
        mealTimestamp: meal.timestamp,
        computedMealDate: mealDate,
        matches: mealDate === selectedDate
      });
    }

    return mealDate === selectedDate;
  });

  // Further filter by meal context if not 'all'
  const filteredMeals = filterContext === 'all'
    ? mealsForSelectedDate
    : mealsForSelectedDate.filter(meal =>
        meal.meal_context?.toLowerCase() === filterContext.toLowerCase()
      );

  // Calculate daily totals from filtered meals
  const dailyTotals = filteredMeals.reduce(
    (totals, meal) => {
      const nutrition = meal.nutrition_totals || {};
      return {
        calories: totals.calories + (nutrition.calories || 0),
        protein: totals.protein + (nutrition.protein || 0),
        carbs: totals.carbs + (nutrition.carbs || 0),
        fat: totals.fat + (nutrition.fat || 0),
        fiber: totals.fiber + (nutrition.fiber || 0)
      };
    },
    { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 }
  );

  // Get recommendations from client profile
  const recommendations = clientProfile?.nutrient_recommendations
    ? (typeof clientProfile.nutrient_recommendations === 'string'
        ? JSON.parse(clientProfile.nutrient_recommendations)
        : clientProfile.nutrient_recommendations)
    : null;

  // Calculate percentage of daily goals
  const calculatePercentage = (actual: number, recommended: number) => {
    if (!recommended || recommended === 0) return 0;
    return Math.min(Math.round((actual / recommended) * 100), 150); // Cap at 150%
  };

  const getContextIcon = (context: string) => {
    const icons: { [key: string]: string } = {
      breakfast: '🌅',
      lunch: '☀️',
      dinner: '🌙',
      snack: '🍎'
    };
    return icons[context?.toLowerCase()] || '🍽️';
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDateChange = (direction: 'prev' | 'next' | 'today') => {
    const currentDate = new Date(selectedDate);

    if (direction === 'prev') {
      currentDate.setDate(currentDate.getDate() - 1);
    } else if (direction === 'next') {
      currentDate.setDate(currentDate.getDate() + 1);
    } else {
      setSelectedDate(new Date().toISOString().split('T')[0]);
      return;
    }

    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const formatDisplayDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  // Handler Functions
  const handleEditMeal = (meal: Meal) => {
    setSelectedMeal(meal);
    setShowEditModal(true);
  };

  const handleAddNote = (meal: Meal) => {
    setSelectedMeal(meal);
    setCoachNote('');
    setShowNoteModal(true);
  };

  const handleDeleteMeal = (meal: Meal) => {
    setSelectedMeal(meal);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMeal = async () => {
    if (!selectedMeal) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/coach/clients/${clientId}/meals/${selectedMeal.id}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = await response.json();

      if (data.success) {
        // Remove meal from state
        setMeals(meals.filter(m => m.id !== selectedMeal.id));
        setShowDeleteConfirm(false);
        setSelectedMeal(null);
      } else {
        alert('Failed to delete meal: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error deleting meal:', err);
      alert('Failed to delete meal');
    }
  };

  const saveCoachNote = async () => {
    if (!selectedMeal || !coachNote.trim()) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/coach/clients/${clientId}/meals/${selectedMeal.id}`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            coach_notes: coachNote
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        // Update meal in state with new note
        setMeals(meals.map(m =>
          m.id === selectedMeal.id
            ? { ...m, coach_notes: coachNote }
            : m
        ));
        setShowNoteModal(false);
        setSelectedMeal(null);
        setCoachNote('');
      } else {
        alert('Failed to save note: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error saving note:', err);
      alert('Failed to save note');
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setShowReportModal(true);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/coach/clients/${clientId}/daily-report-chatbot`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            'X-Session-Id': sessionId
          },
          body: JSON.stringify({
            date: selectedDate,
            meals: mealsForSelectedDate,
            daily_totals: dailyTotals,
            recommendations: recommendations,
            client_profile: clientProfile
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        setReportData(data.report);
      } else {
        alert('Failed to generate report: ' + (data.error || 'Unknown error'));
        setShowReportModal(false);
      }
    } catch (err) {
      console.error('Error generating report:', err);
      alert('Failed to generate report');
      setShowReportModal(false);
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleSendReport = async () => {
    if (!reportData) return;

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/coach/clients/${clientId}/send-daily-report`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            'X-Session-Id': sessionId
          },
          body: JSON.stringify({
            report: reportData,
            date: selectedDate
          })
        }
      );

      const data = await response.json();

      if (data.success) {
        alert('Report sent successfully!');
        setShowReportModal(false);
        setReportData(null);
      } else {
        alert('Failed to send report: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error sending report:', err);
      alert('Failed to send report');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Daily Food Entries
          </h1>
          <p className="text-gray-600">
            View and manage {coachMode ? "client's" : "your"} daily meal logs
          </p>
        </div>

        {/* Generate Report Button - Only for coaches */}
        {coachMode && mealsForSelectedDate.length > 0 && (
          <button
            onClick={handleGenerateReport}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
          >
            <span>📊</span>
            Generate Daily Report
          </button>
        )}
      </div>

      {/* Date Picker Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          {/* Date Navigation */}
          <div className="flex items-center space-x-4">
            <button
              onClick={() => handleDateChange('prev')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Previous day"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">
                {formatDisplayDate(selectedDate)}
              </div>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="mt-2 px-3 py-1 border border-gray-300 rounded-md text-sm"
              />
            </div>

            <button
              onClick={() => handleDateChange('next')}
              disabled={selectedDate >= new Date().toISOString().split('T')[0]}
              className={`p-2 rounded-full transition-colors ${
                selectedDate >= new Date().toISOString().split('T')[0]
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-100'
              }`}
              title="Next day"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <button
            onClick={() => handleDateChange('today')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Today
          </button>
        </div>

        {/* Meal Context Filter */}
        <div className="mt-6 flex items-center space-x-2">
          <span className="text-sm text-gray-600 font-medium">Filter:</span>
          {['all', 'breakfast', 'lunch', 'dinner', 'snack'].map(context => (
            <button
              key={context}
              onClick={() => setFilterContext(context)}
              className={`px-3 py-1 rounded-full text-sm transition-colors ${
                filterContext === context
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {context === 'all' ? '🍽️ All' : `${getContextIcon(context)} ${context.charAt(0).toUpperCase() + context.slice(1)}`}
            </button>
          ))}
        </div>
      </div>

      {/* Today's Totals - QuickStats */}
      {filteredMeals.length > 0 && (
        <div className="mb-6">
          <QuickStats
            nutritionData={null}
            goalsData={null}
            todayData={{
              calories: dailyTotals.calories,
              protein: dailyTotals.protein,
              carbohydrates: dailyTotals.carbs,
              fat: dailyTotals.fat,
              meal_count: filteredMeals.length
            }}
            yesterdayData={null}
          />
        </div>
      )}

      {/* Daily Summary Card */}
      {recommendations && filteredMeals.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Daily Summary</h2>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Calories */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Calories</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(dailyTotals.calories)} / {Math.round(recommendations.daily_calorie_rec || 0)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all"
                  style={{ width: `${calculatePercentage(dailyTotals.calories, recommendations.daily_calorie_rec)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {calculatePercentage(dailyTotals.calories, recommendations.daily_calorie_rec)}%
              </div>
            </div>

            {/* Protein */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Protein</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(dailyTotals.protein)}g / {Math.round(recommendations.protein_rec || 0)}g
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all"
                  style={{ width: `${calculatePercentage(dailyTotals.protein, recommendations.protein_rec)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {calculatePercentage(dailyTotals.protein, recommendations.protein_rec)}%
              </div>
            </div>

            {/* Carbs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Carbs</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(dailyTotals.carbs)}g / {Math.round(recommendations.carbs_rec || 0)}g
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-yellow-600 h-2 rounded-full transition-all"
                  style={{ width: `${calculatePercentage(dailyTotals.carbs, recommendations.carbs_rec)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {calculatePercentage(dailyTotals.carbs, recommendations.carbs_rec)}%
              </div>
            </div>

            {/* Fat */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Fat</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(dailyTotals.fat)}g / {Math.round(recommendations.fat_rec || 0)}g
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-orange-600 h-2 rounded-full transition-all"
                  style={{ width: `${calculatePercentage(dailyTotals.fat, recommendations.fat_rec)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {calculatePercentage(dailyTotals.fat, recommendations.fat_rec)}%
              </div>
            </div>

            {/* Fibre */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Fibre</span>
                <span className="text-sm font-medium text-gray-900">
                  {Math.round(dailyTotals.fiber)}g / {Math.round(recommendations.fiber_rec || 0)}g
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${calculatePercentage(dailyTotals.fiber, recommendations.fiber_rec)}%` }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {calculatePercentage(dailyTotals.fiber, recommendations.fiber_rec)}%
              </div>
            </div>
          </div>

          {/* Summary Stats */}
          <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {filteredMeals.length} {filteredMeals.length === 1 ? 'meal' : 'meals'} logged
            </div>
            <div className="text-sm text-gray-600">
              {filterContext !== 'all' && (
                <span className="text-blue-600 font-medium">
                  Filtered by {filterContext}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Planned vs Actual Comparison Toggle */}
      <div className="mb-6">
        <button
          onClick={() => setShowComparisonView(!showComparisonView)}
          className={`w-full px-4 py-3 rounded-lg border transition-all flex items-center justify-between ${
            showComparisonView
              ? 'bg-blue-50 border-blue-300 text-blue-800'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xl">📊</span>
            <span className="font-medium">Planned vs Actual Comparison</span>
            <span className="text-sm text-gray-500">
              {showComparisonView ? '(Click to hide)' : '(Click to show meal plan adherence)'}
            </span>
          </div>
          <svg
            className={`w-5 h-5 transform transition-transform ${showComparisonView ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Comparison View Panel */}
        {showComparisonView && (
          <div className="mt-4">
            <DailyComparisonView
              initialDate={selectedDate}
              coachMode={coachMode}
              clientId={clientId}
            />
          </div>
        )}
      </div>

      {/* Meals List */}
      <div className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {filteredMeals.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <div className="text-6xl mb-4">🍽️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No meals logged
            </h3>
            <p className="text-gray-600">
              {filterContext === 'all'
                ? `No meals were logged on ${formatDisplayDate(selectedDate)}.`
                : `No ${filterContext} meals were logged on ${formatDisplayDate(selectedDate)}.`}
            </p>
          </div>
        ) : (
          filteredMeals.map((meal) => (
            <div
              key={meal.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                {/* Meal Image */}
                {meal.meal_image_path && (
                  <div className="flex-shrink-0">
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL}${meal.meal_image_path}`}
                      alt={meal.meal_description || 'Meal'}
                      className="w-40 h-40 object-cover rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Meal Details */}
                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        {getContextIcon(meal.meal_context)}
                        {meal.meal_description || 'Meal'}
                      </h3>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm text-gray-500">
                          {formatTime(meal.timestamp)}
                        </span>
                        {meal.meal_context && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                            {meal.meal_context}
                          </span>
                        )}
                        {meal.recognition_confidence && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            {Math.round(meal.recognition_confidence * 100)}% confidence
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Calories Badge */}
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">
                        {Math.round(meal.nutrition_totals?.calories || 0)}
                      </div>
                      <div className="text-xs text-gray-500">calories</div>
                    </div>
                  </div>

                  {/* Macros */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">{Math.round(meal.nutrition_totals?.protein || 0)}g</span> protein
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">{Math.round(meal.nutrition_totals?.carbs || 0)}g</span> carbs
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 bg-orange-500 rounded-full"></span>
                      <span className="text-sm text-gray-700">
                        <span className="font-medium">{Math.round(meal.nutrition_totals?.fat || 0)}g</span> fat
                      </span>
                    </div>
                    {meal.nutrition_totals?.fiber > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="w-3 h-3 bg-purple-500 rounded-full"></span>
                        <span className="text-sm text-gray-700">
                          <span className="font-medium">{Math.round(meal.nutrition_totals?.fiber || 0)}g</span> fiber
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Foods List */}
                  {meal.analyzed_foods && meal.analyzed_foods.length > 0 && (
                    <div className="mb-4">
                      <div className="text-sm text-gray-600 font-medium mb-2">
                        Foods ({meal.analyzed_foods.length}):
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {meal.analyzed_foods.map((food, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800"
                          >
                            {food.name}
                            {food.weight_grams && (
                              <span className="ml-1 text-gray-500 text-xs">
                                ({food.weight_grams}g)
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Coach Actions */}
                  {coachMode && (
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => handleEditMeal(meal)}
                        className="px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Quick Edit"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleAddNote(meal)}
                        className="px-3 py-1.5 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors"
                        title="Add Note"
                      >
                        📝 Add Note
                      </button>
                      <button
                        onClick={() => handleDeleteMeal(meal)}
                        className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete Meal"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedMeal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Meal?
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>{selectedMeal.meal_description || 'this meal'}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setSelectedMeal(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteMeal}
                className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Note Modal */}
      {showNoteModal && selectedMeal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Add Coach Note
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Meal: <strong>{selectedMeal.meal_description || 'Untitled'}</strong>
            </p>
            <textarea
              value={coachNote}
              onChange={(e) => setCoachNote(e.target.value)}
              placeholder="Enter your coaching notes here..."
              className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowNoteModal(false);
                  setSelectedMeal(null);
                  setCoachNote('');
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveCoachNote}
                disabled={!coachNote.trim()}
                className="px-4 py-2 text-white bg-green-600 rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Meal Modal (Placeholder for now) */}
      {showEditModal && selectedMeal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Quick Edit Meal
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Meal: <strong>{selectedMeal.meal_description || 'Untitled'}</strong>
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                <strong>Coming soon:</strong> Quick edit functionality will allow you to modify food items,
                adjust weights, and recalculate nutrition values.
              </p>
            </div>

            {/* Display current meal details */}
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Current Foods:</h4>
                <div className="space-y-2">
                  {selectedMeal.analyzed_foods.map((food, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm">{food.name}</span>
                      <span className="text-sm text-gray-600">{food.weight_grams}g</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-900 mb-2">Nutrition Totals:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Calories: {Math.round(selectedMeal.nutrition_totals?.calories || 0)}</div>
                  <div>Protein: {Math.round(selectedMeal.nutrition_totals?.protein || 0)}g</div>
                  <div>Carbs: {Math.round(selectedMeal.nutrition_totals?.carbs || 0)}g</div>
                  <div>Fat: {Math.round(selectedMeal.nutrition_totals?.fat || 0)}g</div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedMeal(null);
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Daily Report Generation Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
              <h3 className="text-2xl font-bold mb-2">
                📊 Daily Nutrition Report
              </h3>
              <p className="text-purple-100">
                {formatDisplayDate(selectedDate)} • {user?.full_name || 'Client'}
              </p>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {generatingReport ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
                  <p className="text-gray-600 text-lg">Generating your personalized report...</p>
                  <p className="text-gray-500 text-sm mt-2">AI is analyzing nutrition data and creating insights</p>
                </div>
              ) : reportData ? (
                <div className="space-y-6">
                  {/* Report Preview */}
                  <div className="prose max-w-none">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">
                        Good Morning! ☀️
                      </h4>
                      <p className="text-gray-700">
                        Here's your personalized nutrition summary for {formatDisplayDate(selectedDate)}
                      </p>
                    </div>

                    {/* Daily Summary */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
                      <h5 className="text-lg font-semibold text-gray-900 mb-4">📈 Daily Summary</h5>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-blue-50 rounded-lg">
                          <div className="text-2xl font-bold text-blue-600">
                            {Math.round(dailyTotals.calories)}
                          </div>
                          <div className="text-xs text-gray-600">Calories</div>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <div className="text-2xl font-bold text-green-600">
                            {Math.round(dailyTotals.protein)}g
                          </div>
                          <div className="text-xs text-gray-600">Protein</div>
                        </div>
                        <div className="text-center p-3 bg-yellow-50 rounded-lg">
                          <div className="text-2xl font-bold text-yellow-600">
                            {Math.round(dailyTotals.carbs)}g
                          </div>
                          <div className="text-xs text-gray-600">Carbs</div>
                        </div>
                        <div className="text-center p-3 bg-orange-50 rounded-lg">
                          <div className="text-2xl font-bold text-orange-600">
                            {Math.round(dailyTotals.fat)}g
                          </div>
                          <div className="text-xs text-gray-600">Fat</div>
                        </div>
                      </div>
                    </div>

                    {/* AI Insights - Will be populated by backend */}
                    {reportData.insights && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-4">
                        <h5 className="text-lg font-semibold text-gray-900 mb-3">💡 AI Insights</h5>
                        <div className="space-y-2 text-sm text-gray-700">
                          {reportData.insights.map((insight: string, index: number) => (
                            <div key={index} className="flex items-start gap-2">
                              <span className="text-purple-600 mt-1">•</span>
                              <span>{insight}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Meals Overview */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6">
                      <h5 className="text-lg font-semibold text-gray-900 mb-4">🍽️ Meals Logged</h5>
                      <div className="space-y-3">
                        {mealsForSelectedDate.map((meal) => (
                          <div key={meal.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                            <div className="flex items-center gap-3">
                              <span className="text-2xl">{getContextIcon(meal.meal_context)}</span>
                              <div>
                                <div className="font-medium text-sm">{meal.meal_description}</div>
                                <div className="text-xs text-gray-500">{formatTime(meal.timestamp)}</div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-sm">
                                {Math.round(meal.nutrition_totals?.calories || 0)} cal
                              </div>
                              <div className="text-xs text-gray-500">
                                P: {Math.round(meal.nutrition_totals?.protein || 0)}g
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Placeholder for additional sections */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                      <p className="text-sm text-gray-600">
                        Additional insights, recommendations, and analytics will be generated by AI
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 p-6 bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => {
                  setShowReportModal(false);
                  setReportData(null);
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>

              {reportData && !generatingReport && (
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      // Download as PDF functionality can be added later
                      alert('Download PDF feature coming soon!');
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    💾 Download PDF
                  </button>
                  <button
                    onClick={handleSendReport}
                    className="px-6 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-md hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md"
                  >
                    📧 Send to Client
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
